import React, { useMemo } from "react";
import {
  Badge,
  Box,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Spinner,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";

export default function WaitlistMessagingPreferences({
  loading,
  daysAway,
  setDaysAway,
  openSlotDaysAway,
  setOpenSlotDaysAway,
  frequencyDays,
  setFrequencyDays,
  products,
  enabledProductIds,
  onToggleProduct,
  showMessages = false,
  copy = {},
}) {
  const enabledProducts = useMemo(
    () => products.filter((product) => enabledProductIds.has(product.id)),
    [products, enabledProductIds]
  );

  const disabledProducts = useMemo(
    () => products.filter((product) => !enabledProductIds.has(product.id)),
    [products, enabledProductIds]
  );
  const customersReceiveCopy = copy.customers_receive || {};

  return (
    <VStack align="stretch" spacing={6}>
      {showMessages && (
        <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={5} bg="white">
          <HStack justify="space-between" align="center" mb={3}>
            <Text fontSize="sm" color="gray.700" fontWeight="semibold">
              Messaging Templates (Read-only)
            </Text>
            <Badge colorScheme="purple" variant="subtle" fontSize="xs">
              Locked for deliverability
            </Badge>
          </HStack>
          <Text fontSize="xs" color="gray.500" mb={3}>
            These messages are fixed to protect deliverability and avoid spam filters.
          </Text>
          <Box
            display="grid"
            gridTemplateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }}
            gap={4}
          >
            <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={4} bg="gray.50">
              <Text fontSize="xs" color="gray.500" mb={1}>
                {customersReceiveCopy.confirmation_label || "Waitlist confirmation"}
              </Text>
              <Text fontSize="sm" color="gray.700">
                {customersReceiveCopy.confirmation_preview || "\"Preview unavailable.\""}
              </Text>
            </Box>
            <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={4} bg="gray.50">
              <Text fontSize="xs" color="gray.500" mb={1}>
                {customersReceiveCopy.offer_label || "Open slot offer"}
              </Text>
              <Text fontSize="sm" color="gray.700">
                {customersReceiveCopy.offer_preview || "\"Preview unavailable.\""}
              </Text>
            </Box>
          </Box>
        </Box>
      )}
      <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={5} bg="white">
        <VStack align="stretch" spacing={5}>
          <Text fontSize="sm" color="gray.700" fontWeight="semibold">
            Message someone on the waitlist about an open slot, when…
          </Text>
          <Box
            display="grid"
            gridTemplateColumns={{ base: "repeat(3, minmax(0, 1fr))" }}
            gap={6}
          >
            <FormControl>
              <FormLabel fontSize="sm" color="gray.700">Their existing appointment is greater than</FormLabel>
              <HStack spacing={2} align="center">
                <Input
                  type="number"
                  min={1}
                  max={365}
                  value={daysAway}
                  onChange={(e) => setDaysAway(parseInt(e.target.value, 10) || 1)}
                  maxW="140px"
                />
                <Text fontSize="sm" color="gray.600">days away</Text>
              </HStack>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" color="gray.700">The open slot is less than</FormLabel>
              <HStack spacing={2} align="center">
                <Input
                  type="number"
                  min={1}
                  max={365}
                  value={openSlotDaysAway}
                  onChange={(e) => setOpenSlotDaysAway(parseInt(e.target.value, 10) || 1)}
                  maxW="140px"
                />
                <Text fontSize="sm" color="gray.600">days away</Text>
              </HStack>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" color="gray.700">We have not pinged them about open slots in the past</FormLabel>
              <HStack spacing={2} align="center">
                <Input
                  type="number"
                  min={1}
                  max={365}
                  value={frequencyDays}
                  onChange={(e) => setFrequencyDays(parseInt(e.target.value, 10) || 1)}
                  maxW="140px"
                />
                <Text fontSize="sm" color="gray.600">days</Text>
              </HStack>
              <Text fontSize="xs" color="gray.500" mt={1}>
                This is the default. Customers can adjust their own frequency later.
              </Text>
            </FormControl>
          </Box>
        </VStack>
      </Box>

      <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={5} bg="white">
        <Text fontSize="sm" color="gray.700" fontWeight="semibold" mb={2}>
          Products/Services to Text About
        </Text>
        <Text fontSize="xs" color="gray.500" mb={4}>
          All products are enabled by default. Click the X to disable.
        </Text>

        {loading ? (
          <HStack justify="center" py={6}>
            <Spinner size="md" color="blue.500" />
          </HStack>
        ) : (
          <>
            <Wrap spacing={2} mb={4}>
              {enabledProducts.map((product) => (
                <WrapItem key={product.id}>
                  <Tag size="md" colorScheme="blue" borderRadius="full">
                    <TagLabel>{product.name}</TagLabel>
                    <TagCloseButton onClick={() => onToggleProduct(product.id)} />
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>

            {disabledProducts.length > 0 && (
              <>
                <Text fontSize="xs" color="gray.500" mb={2}>
                  Disabled (click to re-enable)
                </Text>
                <Wrap spacing={2}>
                  {disabledProducts.map((product) => (
                    <WrapItem key={product.id}>
                      <Tag
                        size="md"
                        variant="subtle"
                        colorScheme="gray"
                        borderRadius="full"
                        cursor="pointer"
                        onClick={() => onToggleProduct(product.id)}
                      >
                        <TagLabel>{product.name}</TagLabel>
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              </>
            )}
          </>
        )}
      </Box>
    </VStack>
  );
}
